import { z } from "zod";

import { IFilterWithValues } from "@/pages/Private/pages/List/components/FilterModal";

import { ScoreResponse } from "../../schema/score.schema";

/**
 * The General settings form (PREC-4055) and its two directions: the score →
 * form values the page edits, and form values → the score body `PATCH /score`
 * takes. Pure, so the page only wires inputs.
 *
 * The free-text "disqualify companies" rule lives inside `configuration` as the
 * `aiResearchAgent` pseudo-filter; the form lifts it out into its own field so
 * the structured filters and the text are edited independently.
 */

const AI_RESEARCH_AGENT = "aiResearchAgent";

/** The filter records are opaque here: the filter hook owns their shape. */
const isFilterRecord = (value: unknown): value is IFilterWithValues =>
	typeof value === "object" && value !== null && !Array.isArray(value);

export const GeneralFormSchema = z.object({
	serviceDescription: z.string(),
	competitorDescription: z.string(),
	disqualifyCompaniesText: z.string(),
	language: z.string().nullable(),
	configuration: z.custom<IFilterWithValues>(isFilterRecord),
	personaConfiguration: z.custom<IFilterWithValues>(isFilterRecord),
});

export type GeneralFormValues = z.infer<typeof GeneralFormSchema>;

const filtersOnly = (configuration: IFilterWithValues | null | undefined): IFilterWithValues => {
	const { [AI_RESEARCH_AGENT]: _text, ...filters } = configuration ?? {};

	return filters;
};

const aiTextOf = (configuration: IFilterWithValues | null | undefined): string => {
	const value = configuration?.[AI_RESEARCH_AGENT]?.value;

	return typeof value === "string" ? value : "";
};

export const toGeneralForm = (score: ScoreResponse): GeneralFormValues => ({
	serviceDescription: score.serviceDescription ?? "",
	competitorDescription: score.competitorDescription ?? "",
	disqualifyCompaniesText: aiTextOf(score.configuration),
	language: score.language ?? null,
	configuration: filtersOnly(score.configuration),
	personaConfiguration: { ...(score.personaConfiguration ?? {}) },
});

const nullIfBlank = (value: string): string | null => (value.trim() ? value.trim() : null);

/** The score with the form folded back in; blank text fields become null, the API's "unset". */
export const applyGeneralForm = (
	score: ScoreResponse,
	values: GeneralFormValues
): ScoreResponse => {
	const text = nullIfBlank(values.disqualifyCompaniesText);

	return {
		...score,
		serviceDescription: nullIfBlank(values.serviceDescription),
		competitorDescription: nullIfBlank(values.competitorDescription),
		language: values.language,
		configuration: {
			...values.configuration,
			...(text
				? {
						[AI_RESEARCH_AGENT]: {
							value: text,
							filterParam: AI_RESEARCH_AGENT,
							type: "textIsExactly",
						},
					}
				: {}),
		},
		personaConfiguration: { ...values.personaConfiguration },
	};
};

export const isGeneralDirty = (current: GeneralFormValues, saved: GeneralFormValues): boolean =>
	JSON.stringify(current) !== JSON.stringify(saved);
